import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';
import type { UserHighlightsDto } from '@/hooks/useHighlights';

/**
 * "Eure Heldenchronik" Start-Page card (F.4, Cluster 4 Sub-Phase C
 * 2026-05-08). Renders per-child levels + family-aggregated story
 * total + favorite world. Briefing-locked emojis + wording per
 * tracker line 1696-1716 (Sebastian Gate-1 2026-04-27,
 * architectural-correction 2026-05-07).
 *
 * Render contract:
 *   - data prop is the resolved API payload (caller's responsibility
 *     to pass null for hidden state — useHighlights returns null
 *     on hideCard=true / loading / error per locked decisions).
 *   - Defensive double-check: returns null if data.hideCard is true.
 *   - Children rendering:
 *     * 1 child:    "🦸 {name}: {levelLabel} (Lvl {N})"
 *     * 2+ children: "🦸 {name1}: Lvl {N1} · {name2}: Lvl {N2}" (top-2)
 *   - favoriteWorld === null → 🎯 line is omitted entirely.
 *
 * Visual: matches TipSection's bordered-card treatment (white bg,
 * dream-gold border, rounded-lg, p-4) for visual cohesion with the
 * existing Start-Page chrome cards.
 */
export interface HighlightsCardProps {
  data: UserHighlightsDto;
  className?: string;
  testId?: string;
}

export function HighlightsCard({ data, className, testId }: HighlightsCardProps) {
  if (data.hideCard) return null;

  const S = STRINGS.startPage.highlights;
  const childrenLine = renderChildrenLine(data.children, S);

  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-lg border border-dream-gold bg-dream-white p-4',
        className,
      )}
      data-testid={testId}
    >
      <p className="text-sm font-semibold leading-[14px] text-dream-navy">{S.heading}</p>
      {childrenLine && (
        <p className="text-xs leading-5 text-dream-navy" data-testid="highlights-children-line">
          {childrenLine}
        </p>
      )}
      <p className="text-xs leading-5 text-dream-navy" data-testid="highlights-stories-line">
        {S.storiesLine.replace('{totalStories}', String(data.totalStories))}
      </p>
      {data.favoriteWorld !== null && (
        <p
          className="text-xs leading-5 text-dream-navy"
          data-testid="highlights-favorite-world-line"
        >
          {S.favoriteWorldLine.replace('{favoriteWorld}', data.favoriteWorld)}
        </p>
      )}
    </div>
  );
}

function renderChildrenLine(
  children: UserHighlightsDto['children'],
  S: typeof STRINGS.startPage.highlights,
): string | null {
  if (children.length === 0) return null;
  if (children.length === 1) {
    const c = children[0];
    if (!c) return null;
    return S.childLineSingle
      .replace('{childName}', c.childName)
      .replace('{levelLabel}', c.levelLabel)
      .replace('{levelNumber}', String(c.levelNumber));
  }
  // 2+ children: backend caps at top-2 already, but defensively
  // take the first two regardless of array length.
  const c1 = children[0];
  const c2 = children[1];
  if (!c1 || !c2) return null;
  return S.childLineMulti
    .replace('{child1Name}', c1.childName)
    .replace('{N1}', String(c1.levelNumber))
    .replace('{child2Name}', c2.childName)
    .replace('{N2}', String(c2.levelNumber));
}
